import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function SimpleModal({
  show,
  title,
  children,
  footer,
  onClose,
  size = 'lg',
  modalId,
}: {
  show: boolean;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  modalId?: string;
}) {
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [show, onClose]);

  if (!show) return null;

  return createPortal(
    <div
      id={modalId}
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`modal-dialog modal-dialog-centered modal-dialog-scrollable modal-${size}`}>
        <div className="modal-content border-0 shadow">
          <div className="modal-header border-bottom align-items-center py-3">
            <h5 className="modal-title fw-semibold mb-0">{title}</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body px-4 py-3">{children}</div>
          {footer != null ? (
            <div className="modal-footer border-top bg-light bg-opacity-25 px-4 py-3">{footer}</div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
